/**
 * `/inventory/stocktakes` — BLU-711 A2.
 *
 * A 3PL owner's count against a client's rows lands here as a PROPOSAL the
 * client reviews, instead of writing straight to `sku_inventory`. This page is
 * the orchestrator: it owns the tab/selection/triage/messaging state machine
 * and hands each pane a flat prop bag.
 *
 * Two shell rules carry the design:
 *   1. A flagged count has NO Approve button anywhere. The only route is
 *      "Review N flags", which opens the step-through. (The server refuses an
 *      approve with an unresolved flag; this is the affordance.)
 *   2. Opening Messages collapses the SESSION LIST, never the variance table.
 *      When you are discussing a count you need the numbers.
 *
 * Shell mirrors the shipped BLU-665 client-orders inbox: a fixed-width left
 * list plus a detail pane, switched on CONTAINER width (the sidebar can be
 * collapsed at runtime, so a viewport media query lies).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare, Plus, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { PaneResizeHandle } from '@/components/layout/PaneResizeHandle';
import { PageErrorBoundary } from '@/components/fallbacks';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useContainerWidth } from '@/hooks/useContainerWidth';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useRole } from '@/hooks/use-role';
import { useDateFnsLocale } from '@/lib/format-dates';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import BulkStocktakeWizardModal from '@/features/inventory/components/bulk-stocktake-upload/BulkStocktakeWizardModal';
import { useStocktakeExport } from '@/features/inventory/hooks/useStocktakeExport';
import type { Product } from '@/features/inventory/types';
import { parseStocktakeDrift } from '@/lib/trpc-error-utils';
import type {
  StocktakeDriftedLine,
  StocktakeLineDecision,
  StocktakeProposalSummary,
  StocktakeProposalTab,
} from '@threadheads/shared';
import { StocktakeList } from '@/features/stocktakes/components/StocktakeList';
import { StocktakeDetail } from '@/features/stocktakes/components/StocktakeDetail';
import { StocktakeMessages } from '@/features/stocktakes/components/StocktakeMessages';
import { StocktakeComposer } from '@/features/stocktakes/components/StocktakeComposer';
import { FlagTriage, type TriageResolution } from '@/features/stocktakes/components/FlagTriage';
import {
  ApproveCountDialog,
  BulkApproveDialog,
  DecisionComposerDialog,
  NewCountDialog,
  NotifySettingsDialog,
  RejectCountDialog,
  StockDriftDialog,
} from '@/features/stocktakes/components/StocktakeDialogs';
import type { VarianceTableHandle } from '@/features/stocktakes/components/VarianceTable';
import {
  useCounterpartyName,
  useStocktakeAuthority,
  useStocktakeMutations,
  useStocktakeProposalDetail,
  useStocktakeProposalList,
  useStocktakeTabCounts,
} from '@/features/stocktakes/hooks/useStocktakeProposals';
import {
  useStocktakeMessageActions,
  useStocktakeNotifyConfig,
  useStocktakeThread,
} from '@/features/stocktakes/hooks/useStocktakeMessages';
import { buildFlagCards, type FlagCard } from '@/features/stocktakes/lib/flag-cards';
import {
  askMessageSkus,
  buildAskMessage,
  buildDecisionSummary,
  buildPingText,
  buildRejectMessage,
  decisionSummarySkus,
  type MessageContext,
} from '@/features/stocktakes/lib/messages';
import { notifyLabelFor } from '@/features/stocktakes/lib/notify';
import { formatDate } from '@/features/stocktakes/lib/format';

/** Below this much AVAILABLE width the panes stack (list, then detail). */
const TWO_PANE_MIN_WIDTH = 820;

/** Thread pane width. Drag-resizable (DESIGN.md §20) because how much room the
 *  conversation deserves depends on the conversation: a one-line "yes, damaged"
 *  wants the figures wide, a 25-line decision summary wants the thread wide.
 *  The ceiling is the smaller of 640px and 45% of the shell, so the pane can
 *  never squeeze the numbers it exists to discuss. */
const MESSAGE_PANE_DEFAULT_WIDTH = 348;
const MESSAGE_PANE_MIN_WIDTH = 300;
const MESSAGE_PANE_MAX_WIDTH = 640;
const MESSAGE_PANE_MAX_SHELL_FRACTION = 0.45;
const MESSAGE_PANE_WIDTH_KEY = 'stocktakes.messagesPaneWidth';
const MESSAGE_PANE_ID = 'stocktakes-messages-pane';

/** Literal keys (never built by template) so `check:i18n` can see them. */
const EMPTY_COPY: Record<StocktakeProposalTab, { title: string; description: string }> = {
  pending: {
    title: 'stocktakes.empty.pending.title',
    description: 'stocktakes.empty.pending.description',
  },
  waiting: {
    title: 'stocktakes.empty.waiting.title',
    description: 'stocktakes.empty.waiting.description',
  },
  history: {
    title: 'stocktakes.empty.history.title',
    description: 'stocktakes.empty.history.description',
  },
};

interface TriageState {
  /** Proposal ids queued for triage. More than one comes from a bulk approve. */
  ids: string[];
  index: number;
  step: number;
}

type ComposerState =
  | { kind: 'ask'; card: FlagCard; body: string }
  /** `skus` is the reference the summary sends with it — the rows it names.
   *  Carried on the state so a parked composer (drift dialog) keeps it. */
  | { kind: 'decision'; outcome: 'applied' | 'rejected'; body: string; skus: string[] };

/** A row action waiting on its count's detail before it can open its dialog. */
interface PendingRowAction {
  proposalId: string;
  action: 'approve' | 'reject';
}

/** An approve the server refused because the shelf moved under the count. */
interface DriftState {
  proposalId: string;
  lines: StocktakeDriftedLine[];
}

function StocktakesPage() {
  const { t } = useTranslation('inventory');
  const dateLocale = useDateFnsLocale();
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { can } = useRole();
  const utils = trpc.useUtils();

  // Mirrors the server's own gates so no control on this page is a guaranteed
  // 403: `stocktakeMessages.post` is InventoryWrite, and the notify config is
  // OrgSettingsRead to read / OrgSettingsManage to change.
  const canPostMessages = can('inventory.write');
  const canReadNotify = can('org_settings.read');
  const canManageNotify = can('org_settings.manage');

  usePageTitle(t('stocktakes.title'), t('stocktakes.subtitle'));

  const [tab, setTab] = useState<StocktakeProposalTab>('pending');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [triage, setTriage] = useState<TriageState | null>(null);
  const [ackedUncounted, setAckedUncounted] = useState<ReadonlySet<string>>(new Set());
  const [composer, setComposer] = useState<ComposerState | null>(null);
  /** The decision composer while the notify dialog is up. See `openNotifySettings`. */
  const [parkedComposer, setParkedComposer] = useState<ComposerState | null>(null);
  const [approveTarget, setApproveTarget] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [drift, setDrift] = useState<DriftState | null>(null);
  const [pendingRowAction, setPendingRowAction] = useState<PendingRowAction | null>(null);
  const [bulkIds, setBulkIds] = useState<string[] | null>(null);
  const [sweep, setSweep] = useState<{ done: number; total: number } | null>(null);
  const [newCountOpen, setNewCountOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [preparingSheet, setPreparingSheet] = useState(false);
  const [attachedSkus, setAttachedSkus] = useState<ReadonlySet<string>>(new Set());
  const [highlightedSkus, setHighlightedSkus] = useState<ReadonlySet<string>>(new Set());
  const [focusedSku, setFocusedSku] = useState<string | null>(null);
  /** Bumped on every jump so a repeat jump to the same row replays its glow. */
  const [focusNonce, setFocusNonce] = useState(0);
  const [openReferenceId, setOpenReferenceId] = useState<string | null>(null);

  const [shellRef, shellWidth] = useContainerWidth<HTMLDivElement>();
  const twoPane = shellWidth == null || shellWidth >= TWO_PANE_MIN_WIDTH;
  const varianceRef = useRef<VarianceTableHandle>(null);

  // The stored value is the PREFERENCE; what renders is that preference
  // clamped to the room there is right now. Collapsing the sidebar therefore
  // narrows the pane without overwriting what the user asked for, and it comes
  // back at full width when the room does.
  const [storedPaneWidth, setStoredPaneWidth] = useLocalStorage<number>(
    MESSAGE_PANE_WIDTH_KEY,
    MESSAGE_PANE_DEFAULT_WIDTH,
  );
  const [dragPaneWidth, setDragPaneWidth] = useState<number | null>(null);
  const paneMaxWidth =
    shellWidth == null
      ? MESSAGE_PANE_MAX_WIDTH
      : Math.max(
          MESSAGE_PANE_MIN_WIDTH,
          Math.min(MESSAGE_PANE_MAX_WIDTH, Math.round(shellWidth * MESSAGE_PANE_MAX_SHELL_FRACTION)),
        );
  const preferredPaneWidth =
    dragPaneWidth ??
    (Number.isFinite(storedPaneWidth) ? storedPaneWidth : MESSAGE_PANE_DEFAULT_WIDTH);
  const messagesPaneWidth = Math.min(
    paneMaxWidth,
    Math.max(MESSAGE_PANE_MIN_WIDTH, preferredPaneWidth),
  );

  const commitPaneWidth = (next: number) => {
    setDragPaneWidth(null);
    setStoredPaneWidth(next);
  };

  const locationId = selectedLocation?.id;
  const tabCounts = useStocktakeTabCounts(locationId);
  const listQuery = useStocktakeProposalList(tab, locationId);
  const detailQuery = useStocktakeProposalDetail(selectedId);
  const proposal = detailQuery.data ?? null;

  const counterpartyName = useCounterpartyName();
  const { isClientOrg, hasDecisionRole } = useStocktakeAuthority();
  const mutations = useStocktakeMutations();
  const { exportRows, exportExample } = useStocktakeExport();

  const thread = useStocktakeThread(selectedId, !!selectedId);
  const messageActions = useStocktakeMessageActions(selectedId);
  // Gated, not just hidden: firing this for a Viewer or Warehouse Staff is a
  // guaranteed 403 on every proposal they open.
  const notify = useStocktakeNotifyConfig(selectedId, !!selectedId && canReadNotify);

  const canAct = useCallback(
    (candidate: Pick<StocktakeProposalSummary, 'clientOrganizationId'>) =>
      hasDecisionRole && isClientOrg(candidate),
    [hasDecisionRole, isClientOrg],
  );

  // --- selection ---------------------------------------------------------
  // The action-bar box narrows the LIST only (location / client / file name).
  // SKU search lives on the variance table, where the rows are: one input
  // filtering two panes by different rules read as a bug on both.
  const listRows = useMemo(() => {
    const rows = listQuery.data ?? [];
    const needle = search.toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        row.locationName?.toLowerCase().includes(needle) ||
        counterpartyName(row).toLowerCase().includes(needle) ||
        row.filename?.toLowerCase().includes(needle),
    );
  }, [listQuery.data, search, counterpartyName]);

  // Deep link (?proposal=<id>) — the target a ping's link lands on. Honoured
  // once, and the tab follows the proposal's own status so the row is visible
  // in the list behind it.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const param = searchParams.get('proposal');
    if (!param) {
      deepLinkHandled.current = true;
      return;
    }
    deepLinkHandled.current = true;
    setSelectedId(param);
    setSearchParams(
      (params) => {
        params.delete('proposal');
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const deepLinkTabSynced = useRef(false);
  useEffect(() => {
    if (deepLinkTabSynced.current || !proposal) return;
    deepLinkTabSynced.current = true;
    // 'approved' is the transient mid-apply claim and the server lists it in
    // the PENDING tab, so the row behind a deep link is only visible there.
    const wanted: StocktakeProposalTab =
      proposal.status === 'pending' || proposal.status === 'approved'
        ? 'pending'
        : proposal.status === 'waiting_on_3pl'
          ? 'waiting'
          : 'history';
    setTab(wanted);
  }, [proposal]);

  // Re-anchor the selection when the current row drops out of the visible tab.
  // `detailQuery.isLoading` is part of the guard on purpose: a deep-linked
  // proposal is not in the list yet, and re-anchoring while its detail is
  // still in flight would throw the link away before it ever resolved.
  useEffect(() => {
    if (listQuery.isLoading || detailQuery.isLoading) return;
    setSelectedId((current) => {
      if (current && listRows.some((row) => row.proposalId === current)) return current;
      if (current && detailQuery.data) return current; // deep-linked, off-tab
      return listRows[0]?.proposalId ?? null;
    });
  }, [listRows, listQuery.isLoading, detailQuery.isLoading, detailQuery.data]);

  const selectProposal = (proposalId: string) => {
    setSelectedId(proposalId);
    setTriage(null);
    setComposer(null);
    setParkedComposer(null);
    // A warning about the count you just left is worse than no warning.
    setDrift(null);
    setHighlightedSkus(new Set());
    setFocusedSku(null);
    setOpenReferenceId(null);
    setAttachedSkus(new Set());
  };

  // --- derived -----------------------------------------------------------
  const cards = useMemo(() => (proposal ? buildFlagCards(proposal.lines) : []), [proposal]);
  const isCardDone = useCallback(
    (card: FlagCard) =>
      card.kind === 'uncounted' ? ackedUncounted.has(`${selectedId}:${card.key}`) : card.resolved,
    [ackedUncounted, selectedId],
  );

  const linesBySku = useMemo(() => {
    const map = new Map<string, (typeof cards)[number]['lines'][number]>();
    for (const line of proposal?.lines ?? []) if (line.sku) map.set(line.sku, line);
    return map;
  }, [proposal]);

  const acceptedLineCount = useMemo(
    () =>
      (proposal?.lines ?? []).filter(
        (line) => line.decision === 'accept' && line.countedQty != null,
      ).length,
    [proposal],
  );

  const decisionCounts = useMemo(() => {
    const counts = { accept: 0, recount: 0, reject: 0 };
    for (const line of proposal?.lines ?? []) counts[line.decision] += 1;
    return counts;
  }, [proposal]);

  /**
   * "Clean" is NOTHING LEFT TO DECIDE, not "never tripped a rule": a count
   * whose flags have all been triaged, and a partial count whose only oddity
   * is uncounted lines, both apply in one click. The `status` guard matters
   * now that the pending TAB also carries mid-apply 'approved' rows.
   */
  const cleanPendingIds = useMemo(
    () =>
      tabCounts.pending
        .filter(
          (row) => row.status === 'pending' && row.unresolvedFlagsCount === 0 && canAct(row),
        )
        .map((row) => row.proposalId),
    [tabCounts.pending, canAct],
  );

  const messageContext: MessageContext = useMemo(
    () => ({
      t,
      org: proposal ? counterpartyName(proposal) : '',
      locationName: proposal?.locationName ?? '',
      countedOn: proposal
        ? formatDate(proposal.countedAt ?? proposal.submittedAt ?? proposal.createdAt, dateLocale)
        : '',
    }),
    [t, proposal, counterpartyName, dateLocale],
  );

  const pingText = proposal
    ? buildPingText(messageContext, organization?.name ?? '', proposal.proposalId)
    : '';
  // Null rather than the "In app only" default when the config was never
  // fetched: the composer drops the sentence instead of asserting a setting
  // the caller is not allowed to see.
  const notifyLabel = canReadNotify ? notifyLabelFor(notify.config, t) : null;

  /** Our side of the thread, oldest first, for the waiting-state trail. */
  const ownMessages = useMemo(
    () =>
      thread.messages
        .filter((message) => message.side === 'you' && !!message.body)
        .map((message) => ({
          id: message.id,
          body: message.body,
          createdAt: message.createdAt,
        })),
    [thread.messages],
  );

  // --- messages ----------------------------------------------------------
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!messagesOpen || !selectedId || markedRef.current === selectedId) return;
    markedRef.current = selectedId;
    messageActions.markRead.mutate({ proposalId: selectedId });
    // markRead identity churns per render; the guard above is the real gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesOpen, selectedId]);

  const openMessages = () => {
    setMessagesOpen(true);
    markedRef.current = null;
  };

  const toggleReference = (messageId: string, skus: string[]) => {
    if (openReferenceId === messageId) {
      setOpenReferenceId(null);
      setHighlightedSkus(new Set());
      setFocusedSku(null);
      return;
    }
    setOpenReferenceId(messageId);
    setHighlightedSkus(new Set(skus));
    setFocusedSku(null);
  };

  const jumpToSku = (sku: string) => {
    setFocusedSku(sku);
    setFocusNonce((current) => current + 1);
    varianceRef.current?.jumpToSku(sku);
  };

  // --- notify settings ---------------------------------------------------
  /**
   * The decision composer is a Dialog, and a Dialog opened from a Dialog is
   * banned (DESIGN.md §10). Park the composer, run the notify dialog on its
   * own, then put it back with its text intact.
   */
  const openNotifySettings = () => {
    if (composer?.kind === 'decision') {
      setParkedComposer(composer);
      setComposer(null);
    }
    setNotifyOpen(true);
  };

  const closeNotifySettings = () => {
    setNotifyOpen(false);
    if (parkedComposer) {
      setComposer(parkedComposer);
      setParkedComposer(null);
    }
  };

  const toggleAttachedSku = (sku: string) => {
    setAttachedSkus((current) => {
      const next = new Set(current);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  /** Best effort: a thread that will not accept the message must never block
   *  the approval it accompanies. The mutation surfaces its own error toast. */
  const postMessage = useCallback(
    (body: string, skus: string[] = []) =>
      messageActions.sendMessage(body, skus).catch(() => null),
    [messageActions],
  );
  const postMessageTo = useCallback(
    (targetProposalId: string, body: string, skus: string[] = []) =>
      messageActions.sendMessageTo(targetProposalId, body, skus).catch(() => null),
    [messageActions],
  );

  // --- triage ------------------------------------------------------------
  const nextStep = useCallback(
    (from: number) => {
      for (let index = from; index < cards.length; index += 1) {
        const card = cards[index];
        if (card && !isCardDone(card)) return index;
      }
      return cards.length;
    },
    [cards, isCardDone],
  );

  const openTriage = (ids: string[]) => {
    const first = ids[0];
    if (!first) return;
    setSelectedId(first);
    setComposer(null);
    setTriage({ ids, index: 0, step: 0 });
  };

  // Land on the first flag that still needs a decision, not on a cleared one.
  useEffect(() => {
    if (!triage || cards.length === 0) return;
    setTriage((current) =>
      current && current.step === 0 ? { ...current, step: nextStep(0) } : current,
    );
    // Only re-seed when the queue position changes, never mid-step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triage?.index, cards.length]);

  // Each mutation carries its own error toast, so a failure here just stops the
  // step from advancing: the flag stays open and the reviewer can retry.
  const resolveCard = async (card: FlagCard, resolution: TriageResolution) => {
    if (!selectedId) return;
    try {
      if (card.kind === 'uncounted') {
        setAckedUncounted((current) => new Set(current).add(`${selectedId}:${card.key}`));
      } else {
        // A mirror card stamps BOTH of its lines.
        for (const lineId of card.lineIds) {
          await mutations.resolveFlag.mutateAsync({ proposalId: selectedId, lineId, resolution });
        }
      }
    } catch {
      return;
    }
    setTriage((current) => (current ? { ...current, step: nextStep(current.step + 1) } : current));
  };

  const askAboutCard = (card: FlagCard) => {
    setComposer({ kind: 'ask', card, body: buildAskMessage(card, messageContext) });
  };

  const sendAsk = async () => {
    if (!composer || composer.kind !== 'ask' || !selectedId) return;
    // The generated body names the rows in prose; the attachment makes them
    // clickable, so the other side gets the same jump-to-row chip a manually
    // ticked message carries (owner feedback 2026-08-05).
    await postMessage(composer.body, askMessageSkus(composer.card));
    try {
      for (const lineId of composer.card.lineIds) {
        await mutations.resolveFlag.mutateAsync({
          proposalId: selectedId,
          lineId,
          resolution: 'asked',
        });
      }
      await mutations.setWaiting.mutateAsync({ proposalId: selectedId, waiting: true });
    } catch {
      return;
    }
    setComposer(null);
    setTriage(null);
    setTab('waiting');
  };

  // --- approve / reject --------------------------------------------------
  /**
   * Both confirm dialogs quote the count's OWN figures (accepted lines, net
   * change, location), and those live on the detail. Opening from a LIST ROW
   * means the detail for that row may not be fetched yet, so the dialog is
   * held behind the fetch: without it, it renders "Approve 0 lines" and a
   * Continue that does nothing, because `continueToDecisionComposer` returns
   * early on a null proposal.
   *
   * Already-loaded targets (the detail pane's own buttons, the end of triage)
   * hit the query cache and open in the same tick.
   */
  const openRowAction = async (proposalId: string, action: 'approve' | 'reject') => {
    setSelectedId(proposalId);
    if (proposal?.proposalId !== proposalId) {
      setPendingRowAction({ proposalId, action });
      try {
        await utils.stocktakes.getDetail.fetch({ proposalId });
      } catch {
        toast({
          title: t('stocktakes.error.loadDetail'),
          description: t('stocktakes.error.loadDetailDescription'),
          variant: 'destructive',
        });
        return;
      } finally {
        setPendingRowAction(null);
      }
    }
    if (action === 'approve') setApproveTarget(proposalId);
    else setRejectTarget(proposalId);
  };

  const openApprove = (target: StocktakeProposalSummary | { proposalId: string }) => {
    void openRowAction(target.proposalId, 'approve');
  };

  const openReject = (target: StocktakeProposalSummary | { proposalId: string }) => {
    void openRowAction(target.proposalId, 'reject');
  };

  const continueToDecisionComposer = (outcome: 'applied' | 'rejected') => {
    if (!proposal) return;
    setApproveTarget(null);
    setRejectTarget(null);
    setComposer({
      kind: 'decision',
      outcome,
      body:
        outcome === 'applied'
          ? buildDecisionSummary(proposal.lines, messageContext)
          : buildRejectMessage(messageContext),
      // A rejection sends the whole count back and names no rows, so it
      // attaches none. An approval attaches what it asks for: recount + not
      // accepted.
      skus: outcome === 'applied' ? decisionSummarySkus(proposal.lines) : [],
    });
  };

  const advanceQueue = () => {
    setTriage((current) => {
      if (!current) return null;
      const nextIndex = current.index + 1;
      const nextId = current.ids[nextIndex];
      if (!nextId) return null;
      setSelectedId(nextId);
      return { ids: current.ids, index: nextIndex, step: 0 };
    });
  };

  /**
   * DECISION FIRST, message second. The reverse order posted "here is what we
   * approved" and then left the composer open when the approve failed, so the
   * other side read a summary of something that never happened — and the
   * retry re-posted it. Because the message only goes out after the mutation
   * has committed, the retry path (composer stays open, mutation failed)
   * cannot double-post.
   */
  const finishDecision = async (
    targetId: string,
    body: string,
    outcome: 'applied' | 'rejected',
    skus: string[],
  ) => {
    // Best effort: the decision is committed, so a thread failure must not
    // read as a failed approval. Name what did and did not happen.
    try {
      await messageActions.sendMessageTo(targetId, body, skus);
      toast({
        title: outcome === 'applied' ? t('stocktakes.toast.applied') : t('stocktakes.toast.rejected'),
      });
    } catch {
      toast({
        title:
          outcome === 'applied'
            ? t('stocktakes.toast.appliedNoMessage')
            : t('stocktakes.toast.rejectedNoMessage'),
        description: t('stocktakes.toast.noMessageDescription'),
        variant: 'destructive',
      });
    }
    setComposer(null);
    setPicked((current) => {
      const next = new Set(current);
      next.delete(targetId);
      return next;
    });
    advanceQueue();
  };

  const sendDecision = async () => {
    if (!composer || composer.kind !== 'decision' || !selectedId) return;
    const targetId = selectedId;
    const { body, outcome, skus } = composer;
    try {
      if (outcome === 'applied') {
        await mutations.approve.mutateAsync({ proposalId: targetId });
      } else {
        await mutations.reject.mutateAsync({ proposalId: targetId });
      }
    } catch (error) {
      // The shelf moved between submit and approve. Park the composer (a
      // Dialog opened from a Dialog is banned, DESIGN.md §10) and show what
      // changed; "Approve anyway" resumes exactly here with the reviewer's
      // message intact.
      const { isDrift, driftedLines } = parseStocktakeDrift(error);
      if (isDrift && outcome === 'applied') {
        setParkedComposer(composer);
        setComposer(null);
        setDrift({ proposalId: targetId, lines: driftedLines });
        return;
      }
      // Toasted by the mutation; leave the composer open so the message the
      // reviewer wrote is not lost, and post nothing.
      return;
    }
    await finishDecision(targetId, body, outcome, skus);
  };

  /** Cancelling the drift warning puts the reviewer back where they were: the
   *  count is still pending, and the summary they had written is still there. */
  const dismissDrift = () => {
    setDrift(null);
    if (parkedComposer) {
      setComposer(parkedComposer);
      setParkedComposer(null);
    }
  };

  const approveDespiteDrift = async () => {
    if (!drift) return;
    const targetId = drift.proposalId;
    const parked = parkedComposer;
    try {
      await mutations.approve.mutateAsync({ proposalId: targetId, acknowledgeDrift: true });
    } catch {
      // Toasted by the mutation. Hand the reviewer back the composer rather
      // than stranding them on a warning about an approve that never ran.
      dismissDrift();
      return;
    }
    setDrift(null);
    setParkedComposer(null);
    if (parked?.kind === 'decision') {
      await finishDecision(targetId, parked.body, parked.outcome, parked.skus);
    }
  };

  /**
   * The sweep walks several counts one at a time, which can take a while on a
   * big pending tab. It publishes its position so both bulk entry points can
   * stand down and say how far along it is, instead of looking dead.
   */
  const runBulkApprove = async (ids: string[]) => {
    const summaries = tabCounts.pending.filter(
      (row) => ids.includes(row.proposalId) && row.status === 'pending',
    );
    const clean = summaries.filter((row) => row.unresolvedFlagsCount === 0);
    const flagged = summaries.filter((row) => row.unresolvedFlagsCount > 0);
    setBulkIds(null);
    if (clean.length > 0) setSweep({ done: 0, total: clean.length });

    let applied = 0;
    let drifted = 0;
    try {
      for (const row of clean) {
        try {
          const detail = await utils.stocktakes.getDetail.fetch({ proposalId: row.proposalId });
          await mutations.approve.mutateAsync({ proposalId: row.proposalId });
          applied += 1;
          // Explicit target: the sweep walks several counts, so the summary
          // must land on the count it describes, not on whichever one is open.
          // Posted AFTER the apply, for the same reason as `sendDecision`.
          await postMessageTo(
            row.proposalId,
            buildDecisionSummary(detail.lines, {
              t,
              org: counterpartyName(row),
              locationName: row.locationName ?? '',
              countedOn: formatDate(row.countedAt ?? row.submittedAt ?? row.createdAt, dateLocale),
            }),
            decisionSummarySkus(detail.lines),
          );
        } catch (error) {
          // A drift is the one failure the mutation deliberately does NOT
          // toast (the dialog owns it), and mid-sweep there is no dialog to
          // own it — so count them and say so once at the end. Everything
          // else already toasted; keep going either way so one bad count does
          // not strand the rest of the sweep.
          if (parseStocktakeDrift(error).isDrift) drifted += 1;
        }
        setSweep((current) => (current ? { ...current, done: current.done + 1 } : current));
      }
    } finally {
      setSweep(null);
    }

    setPicked(new Set());
    if (applied > 0) toast({ title: t('stocktakes.toast.bulkApplied', { count: applied }) });
    if (drifted > 0) {
      toast({
        title: t('stocktakes.toast.bulkDrifted', { count: drifted }),
        description: t('stocktakes.toast.bulkDriftedDescription'),
      });
    }
    if (flagged.length > 0) openTriage(flagged.map((row) => row.proposalId));
  };

  // --- count sheet -------------------------------------------------------
  const downloadCountSheet = async () => {
    if (!locationId) {
      toast({
        title: t('stocktakeExport.noLocationTitle'),
        description: t('stocktakeExport.noLocationDesc'),
        variant: 'destructive',
      });
      return;
    }
    setPreparingSheet(true);
    try {
      const items = await utils.inventory.getProducts.fetch({ locationId });
      const rows: Product[] = (items ?? []).map((item) => ({
        id: item.id,
        sku: item.sku,
        title: item.title ?? undefined,
        category: item.category ?? {},
        stockLevel: item.stockLevel,
        productionType: item.productionType ?? 'POD',
        isPreprinted: item.isPreprinted ?? false,
        isTrim: item.isTrim ?? false,
        ownerOrganizationId: item.ownerOrganizationId ?? undefined,
      }));
      exportRows(rows, 'all');
      setNewCountOpen(false);
    } catch {
      // The fetch can fail (offline, a location with thousands of rows timing
      // out). Say so and keep the dialog open on the same action, rather than
      // resetting the spinner and looking like nothing was clicked.
      toast({
        title: t('stocktakeExport.failedTitle'),
        description: t('stocktakeExport.failedDesc'),
        variant: 'destructive',
      });
    } finally {
      setPreparingSheet(false);
    }
  };

  // --- render ------------------------------------------------------------
  // Authority comes off the DETAIL, not the list row: a deep-linked proposal
  // may not be in the visible tab at all, and a read-only detail would be the
  // wrong answer for the org that actually approves it.
  const proposalCounterparty = proposal ? counterpartyName(proposal) : '';
  const locationLabel =
    selectedLocation?.name ?? proposal?.locationName ?? t('stocktakes.row.unknownLocation');
  const isMutating =
    mutations.approve.isPending ||
    mutations.reject.isPending ||
    mutations.setWaiting.isPending ||
    messageActions.isSending;

  const triageNode =
    triage && proposal ? (
      <FlagTriage
        cards={cards}
        stepIndex={triage.step}
        locationName={proposal.locationName ?? locationLabel}
        counterpartyName={proposalCounterparty}
        uncountedCount={proposal.notCountedCount}
        acceptedLineCount={acceptedLineCount}
        queuePosition={
          triage.ids.length > 1
            ? { index: triage.index + 1, total: triage.ids.length }
            : null
        }
        onCancel={() => {
          setTriage(null);
          setComposer(null);
        }}
        onResolve={(card, resolution) => void resolveCard(card, resolution)}
        onAsk={askAboutCard}
        onApprove={() => openApprove({ proposalId: proposal.proposalId })}
        isPending={mutations.resolveFlag.isPending || isMutating}
        composer={
          composer?.kind === 'ask' ? (
            <StocktakeComposer
              title={t('stocktakes.composer.askTitle', { org: proposalCounterparty })}
              hint={t('stocktakes.composer.askHint')}
              cta={t('stocktakes.composer.askCta')}
              value={composer.body}
              onChange={(body) => setComposer({ ...composer, body })}
              onCancel={() => setComposer(null)}
              onSend={() => void sendAsk()}
              pingText={pingText}
              notifyLabel={notifyLabel}
              onChangeNotify={canManageNotify ? openNotifySettings : undefined}
              isSending={isMutating}
            />
          ) : undefined
        }
      />
    ) : null;

  return (
    <div className="flex h-[calc(100dvh-var(--top-header-height))] flex-col overflow-hidden px-4 sm:px-6 lg:px-8">
      <PageActionBar
        ariaLabel={t('stocktakes.actionBarAria')}
        className="border-b-border/60"
        // This page is a fixed-height, non-scrolling shell — a sticky offset
        // here is measured against the page's own overflow-hidden box and
        // shoves the bar down over the panes (see PageActionBar's prop doc).
        sticky={false}
        left={
          <>
            <SegmentedControl
              ariaLabel={t('stocktakes.tabsAria')}
              value={tab}
              onChange={(value) => {
                setTab(value);
                setTriage(null);
                setComposer(null);
                setParkedComposer(null);
                // Picks are rows in the tab you just left: keeping them would
                // let the bulk bar act on counts that are no longer on screen.
                setPicked(new Set());
              }}
              options={[
                {
                  value: 'pending' as StocktakeProposalTab,
                  label: t('stocktakes.tab.pending'),
                  badge: tabCounts.pending.length,
                },
                {
                  value: 'waiting' as StocktakeProposalTab,
                  label: t('stocktakes.tab.waiting'),
                  badge: tabCounts.waiting.length,
                },
                { value: 'history' as StocktakeProposalTab, label: t('stocktakes.tab.history') },
              ]}
            />
            <div className="relative w-56">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t('stocktakes.searchPlaceholder')}
                aria-label={t('stocktakes.searchAria')}
                className="h-8 pl-9 text-xs"
              />
            </div>
          </>
        }
        right={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs"
              disabled={cleanPendingIds.length === 0 || sweep != null}
              onClick={() => setBulkIds(cleanPendingIds)}
              // The label IS the progress readout while the sweep runs.
              aria-live="polite"
            >
              {sweep ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {t('stocktakes.bulk.applying', {
                    current: Math.min(sweep.done + 1, sweep.total),
                    total: sweep.total,
                  })}
                </>
              ) : (
                t('stocktakes.action.approveAllClean', { count: cleanPendingIds.length })
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'relative h-8 gap-1.5 px-3 text-xs',
                messagesOpen &&
                  'border-threadheads-purple/40 bg-threadheads-purple/10 text-threadheads-purple hover:bg-threadheads-purple/15',
              )}
              // A toggle, not just an opener: the thread pane's own Close and
              // the detail header's "Back to list" also exit, but the button
              // that got you in must get you out (owner feedback 2026-08-05).
              onClick={() => (messagesOpen ? setMessagesOpen(false) : openMessages())}
              aria-pressed={messagesOpen}
              disabled={!selectedId}
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
              {t('stocktakes.action.messages')}
              {thread.unreadCount > 0 ? (
                <span className="inline-flex min-w-[17px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold tabular-nums text-destructive-foreground motion-safe:animate-pulse">
                  {thread.unreadCount}
                </span>
              ) : null}
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              onClick={() => setNewCountOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('stocktakes.action.newCount')}
            </Button>
          </>
        }
      />

      <div
        ref={shellRef}
        className={cn(
          '-mx-4 flex min-h-0 flex-1 overflow-hidden border-t sm:-mx-6 lg:-mx-8',
          // Narrow: stack instead of squashing. The thread sits UNDER the
          // figures rather than beside them, so the rule still holds — you can
          // always see the numbers you are discussing.
          !twoPane && 'flex-col',
        )}
      >
        {/* LEFT — session list. Collapses while the thread is open. */}
        {!messagesOpen && (twoPane || !selectedId) ? (
          <section
            aria-label={t('stocktakes.listAria')}
            className={cn('flex min-h-0 flex-col bg-card', twoPane ? 'w-[460px] shrink-0 border-r' : 'w-full')}
          >
            <StocktakeList
              proposals={listRows}
              selectedId={selectedId}
              onSelect={selectProposal}
              picked={picked}
              onTogglePick={(id) =>
                setPicked((current) => {
                  const next = new Set(current);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              onClearPicks={() => setPicked(new Set())}
              onApproveSelected={() => setBulkIds([...picked])}
              onApproveOne={openApprove}
              onReviewFlags={(row) => openTriage([row.proposalId])}
              onRejectOne={openReject}
              pendingRowAction={pendingRowAction}
              sweeping={sweep != null}
              canAct={canAct}
              counterpartyName={counterpartyName}
              isLoading={listQuery.isLoading}
              isError={listQuery.isError}
              onRetry={() => void listQuery.refetch()}
              emptyTitle={t(EMPTY_COPY[tab].title)}
              emptyDescription={t(EMPTY_COPY[tab].description)}
              filtersActive={search.length > 0}
              onClearFilters={() => setSearchInput('')}
            />
          </section>
        ) : null}

        {/* RIGHT — detail. Always visible in message mode. */}
        {twoPane || selectedId ? (
          <section
            aria-label={t('stocktakes.detailAria')}
            className="flex min-w-0 flex-1 flex-col bg-background"
          >
            <StocktakeDetail
              proposal={proposal}
              isLoading={detailQuery.isLoading && !!selectedId}
              isError={detailQuery.isError}
              onRetry={() => void detailQuery.refetch()}
              counterpartyName={proposalCounterparty}
              cards={cards}
              canAct={!!proposal && canAct(proposal)}
              isClientOrg={!!proposal && isClientOrg(proposal)}
              showBack={messagesOpen || !twoPane}
              onBack={() => {
                if (messagesOpen) setMessagesOpen(false);
                else setSelectedId(null);
              }}
              pickable={messagesOpen && canPostMessages}
              selectedSkus={attachedSkus}
              onToggleSku={toggleAttachedSku}
              highlightedSkus={highlightedSkus}
              focusedSku={focusedSku}
              focusNonce={focusNonce}
              varianceRef={varianceRef}
              onDecision={(lineId: string, decision: StocktakeLineDecision) => {
                if (!selectedId) return;
                mutations.setLineDecisions.mutate({
                  proposalId: selectedId,
                  decisions: [{ lineId, decision }],
                });
              }}
              decisionsPending={mutations.setLineDecisions.isPending}
              onApprove={() => proposal && openApprove({ proposalId: proposal.proposalId })}
              onReject={() => proposal && openReject({ proposalId: proposal.proposalId })}
              onReviewFlags={() => proposal && openTriage([proposal.proposalId])}
              onResumeReview={() => {
                if (!selectedId) return;
                mutations.setWaiting.mutate({ proposalId: selectedId, waiting: false });
                setTab('pending');
              }}
              onRetryPush={() => {
                if (!selectedId) return;
                mutations.retryPush.mutate({ proposalId: selectedId });
              }}
              retryPushPending={mutations.retryPush.isPending}
              isMutating={isMutating}
              ownMessages={ownMessages}
              triageNode={triageNode}
            />
          </section>
        ) : null}

        {/* MESSAGES — replaces the list, never the numbers. */}
        {messagesOpen && selectedId ? (
          <>
            {/* Stacked, the thread sits UNDER the figures: there is no vertical
                edge to drag, so the pane is simply full width. */}
            {twoPane ? (
              <PaneResizeHandle
                width={messagesPaneWidth}
                min={MESSAGE_PANE_MIN_WIDTH}
                max={paneMaxWidth}
                side="right"
                onResize={setDragPaneWidth}
                onCommit={commitPaneWidth}
                onReset={() => commitPaneWidth(MESSAGE_PANE_DEFAULT_WIDTH)}
                ariaLabel={t('stocktakes.messages.resizeAria')}
                controlsId={MESSAGE_PANE_ID}
              />
            ) : null}
            <div
              id={MESSAGE_PANE_ID}
              className={cn(
                'min-h-0',
                twoPane ? 'shrink-0' : 'h-[520px] w-full shrink-0 border-t',
              )}
              style={twoPane ? { width: messagesPaneWidth } : undefined}
            >
              <StocktakeMessages
                counterpartyName={proposalCounterparty}
                messages={thread.messages}
                isLoading={thread.isLoading}
                isError={thread.isError}
                onRetry={() => void thread.refetch()}
                linesBySku={linesBySku}
                attachedSkus={[...attachedSkus]}
                onClearAttachments={() => setAttachedSkus(new Set())}
                openReferenceId={openReferenceId}
                onToggleReference={toggleReference}
                focusedSku={focusedSku}
                onJumpToSku={jumpToSku}
                onSend={(body) => {
                  void postMessage(body, [...attachedSkus]);
                  setAttachedSkus(new Set());
                }}
                isSending={messageActions.isSending}
                pingText={pingText}
                onOpenNotifySettings={canManageNotify ? openNotifySettings : undefined}
                canPost={canPostMessages}
                onClose={() => setMessagesOpen(false)}
              />
            </div>
          </>
        ) : null}
      </div>

      {/* --- dialogs (sequential, never stacked) ---
          Both confirms also gate on the DETAIL having landed for the target:
          `openRowAction` already awaits it, and this is the belt to that
          braces — the figures below all come off `proposal`. */}
      <ApproveCountDialog
        open={!!approveTarget && proposal?.proposalId === approveTarget}
        onOpenChange={(open) => !open && setApproveTarget(null)}
        locationName={proposal?.locationName ?? locationLabel}
        counterpartyName={proposalCounterparty}
        acceptCount={acceptedLineCount}
        recountCount={decisionCounts.recount}
        rejectCount={decisionCounts.reject}
        netChange={proposal?.netChange ?? 0}
        onContinue={() => continueToDecisionComposer('applied')}
      />

      <StockDriftDialog
        open={!!drift}
        onOpenChange={(open) => !open && dismissDrift()}
        locationName={proposal?.locationName ?? locationLabel}
        lines={drift?.lines ?? []}
        onApproveAnyway={() => void approveDespiteDrift()}
        isApproving={mutations.approve.isPending}
      />

      <RejectCountDialog
        open={!!rejectTarget && proposal?.proposalId === rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
        locationName={proposal?.locationName ?? locationLabel}
        counterpartyName={proposalCounterparty}
        onContinue={() => continueToDecisionComposer('rejected')}
      />

      <BulkApproveDialog
        open={!!bulkIds && bulkIds.length > 0}
        onOpenChange={(open) => !open && setBulkIds(null)}
        cleanCount={
          (bulkIds ?? []).filter((id) =>
            tabCounts.pending.some(
              (row) =>
                row.proposalId === id &&
                row.status === 'pending' &&
                row.unresolvedFlagsCount === 0,
            ),
          ).length
        }
        flaggedCount={
          (bulkIds ?? []).filter((id) =>
            tabCounts.pending.some(
              (row) =>
                row.proposalId === id && row.status === 'pending' && row.unresolvedFlagsCount > 0,
            ),
          ).length
        }
        onContinue={() => void runBulkApprove(bulkIds ?? [])}
      />

      <DecisionComposerDialog
        open={composer?.kind === 'decision'}
        onOpenChange={(open) => !open && setComposer(null)}
        title={
          composer?.kind === 'decision' && composer.outcome === 'applied'
            ? t('stocktakes.composer.summaryTitle', { org: proposalCounterparty })
            : t('stocktakes.composer.rejectTitle', { org: proposalCounterparty })
        }
        hint={t('stocktakes.composer.summaryHint')}
        cta={
          composer?.kind === 'decision' && composer.outcome === 'applied'
            ? t('stocktakes.composer.sendAndApply')
            : t('stocktakes.composer.sendAndReject')
        }
        value={composer?.kind === 'decision' ? composer.body : ''}
        onChange={(body) =>
          setComposer((current) =>
            current?.kind === 'decision' ? { ...current, body } : current,
          )
        }
        onSend={() => void sendDecision()}
        pingText={pingText}
        notifyLabel={notifyLabel}
        onChangeNotify={canManageNotify ? openNotifySettings : undefined}
        isSending={isMutating}
      />

      <NewCountDialog
        open={newCountOpen}
        onOpenChange={setNewCountOpen}
        locationName={locationLabel}
        onDownloadCountSheet={() => void downloadCountSheet()}
        onDownloadExample={() => {
          exportExample();
          setNewCountOpen(false);
        }}
        onUploadSheet={() => {
          setNewCountOpen(false);
          setWizardOpen(true);
        }}
        isPreparingSheet={preparingSheet}
      />

      {canManageNotify ? (
        <NotifySettingsDialog
          open={notifyOpen}
          onOpenChange={(open) => (open ? setNotifyOpen(true) : closeNotifySettings())}
          counterpartyName={proposalCounterparty}
          config={notify.config}
          pingText={pingText}
          onSave={(mode, channel) => {
            notify.save(mode, channel);
            closeNotifySettings();
          }}
          isSaving={notify.isSaving}
        />
      ) : null}

      {wizardOpen ? (
        <BulkStocktakeWizardModal
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onSuccess={() => {
            void utils.stocktakes.list.invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

export default function StocktakesPageWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <StocktakesPage />
    </PageErrorBoundary>
  );
}
